import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cleanAmountInput, formatAmountInput, isAllowedAmountKey } from './input-text-form.utils';

@Component({
    selector: 'core-ui-input-text-form',
    templateUrl: './input-text-form.component.html',
    styleUrls: ['./input-text-form.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputTextFormComponent),
            multi: true,
        },
    ],
})
export class InputTextFormComponent implements ControlValueAccessor {
    @Input() label?: string;
    @Input() placeholder = '';
    @Input() icon?: string;
    @Input() iconPosition: 'left' | 'right' = 'right';
    @Input() iconColor = '#9CA3AF';
    @Input() prefixText?: string;
    @Input() type: 'text' | 'number' | 'email' | 'password' | 'search' = 'text';
    @Input() inputMode: 'text' | 'amount' = 'text';
    @Input() formatThousands = false;
    @Input() ariaAutocomplete?: string;
    @Input() ariaControls?: string;
    @Input() ariaExpanded?: boolean;
    @Input() ariaActiveDescendant?: string | null;

    @Input() readOnly = false;
    @Input() error = false;

    @Input() block = true;
    @Input() size: 'sm' | 'md' | 'lg' = 'md';
    @Input() sizeIcon: 'sm' | 'md' | 'lg' = 'md';
    @Input() uiMode: 'form' | 'filter' = 'form';

    @Output() focused = new EventEmitter<void>();
    @Output() blurred = new EventEmitter<void>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyPressed = new EventEmitter<KeyboardEvent>();
    @Output() inputChanged = new EventEmitter<HTMLInputElement>();

    @Input()
    set modelValue(v: string | null) {
        this.value = v ?? '';
    }

    @Output() valueChange = new EventEmitter<string>();
    @Output() enter = new EventEmitter<string>();

    value = '';
    isFocused = false;
    disabled = false;

    private onChange: (v: string) => void = () => { };
    private onTouched: () => void = () => { };

    writeValue(v: string | null): void {
        this.value = v ?? '';
    }

    registerOnChange(fn: (v: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    @Input()
    set isDisabled(value: boolean) {
        this.disabled = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    get isReadOnlyFinal(): boolean {
        return this.readOnly || this.disabled;
    }

    handleInput(event: Event): void {
        if (this.disabled || this.readOnly) return;

        const input = event.target as HTMLInputElement;
        const caret = input.selectionStart ?? input.value.length;
        const valueBeforeCaret = input.value.slice(0, caret);
        const nextValue = this.inputMode === 'amount'
            ? this.formatThousands ? formatAmountInput(input.value) : cleanAmountInput(input.value)
            : input.value;

        this.value = nextValue;
        input.value = nextValue;
        if (this.inputMode === 'amount' && this.formatThousands) {
            const nextCaret = formatAmountInput(valueBeforeCaret).length;
            input.setSelectionRange(nextCaret, nextCaret);
        }

        this.onChange(this.value);
        this.valueChange.emit(this.value);
    }

    handleBlur() {
        if (this.inputMode === 'amount' && this.formatThousands && this.value) {
            const amount = Number(this.value.replace(/,/g, ''));
            if (Number.isFinite(amount)) {
                this.value = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                this.onChange(this.value);
                this.valueChange.emit(this.value);
            }
        }
        this.isFocused = false;
        this.onTouched();
        this.blurred.emit();
    }

    handleEnter() {
        if (this.disabled || this.readOnly) return;
        this.enter.emit(this.value);
    }

    handleFocus() {
        if (this.disabled || this.readOnly) return;
        this.isFocused = true;
        this.focused.emit();
    }

    handleKeydown(event: KeyboardEvent) {
        if (this.disabled || this.readOnly) return;

        if (this.inputMode === 'amount' && !isAllowedAmountKey(event)) {
            event.preventDefault();
            return;
        }

        this.keydown.emit(event);
        this.keyPressed.emit(event);
    }

    get isFilled(): boolean {
        return this.uiMode === 'form' && (this.value ?? '').trim().length > 0;
    }
}
